// Vendors
import React, { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { difference, get } from 'lodash'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, FieldError, SubmitHandler, useForm } from 'react-hook-form'

// Functions
import { useRole } from 'hooks/useRole'
import { useCreateRecord } from 'hooks/useCreateRecord'
import { useSelectParser } from 'hooks/useSelectParser'

// Schemas
import { schema } from '../new/schema'

// Components
import { FieldDate } from 'components/FieldDate'
import { FieldMask } from 'components/FieldMask'
import { FieldText } from 'components/FieldText'
import { FormFooter } from 'components/FormFooter'
import { FieldToggle } from 'components/FieldToggle'
import { FieldSelect } from 'components/FieldSelect'
import { FieldRadioButton } from 'components/FieldRadioButton'
import { FormRequirementDocumentsFields } from 'components/FormRequirementDocumentsFields'

// Styles
import { Flex, Grid, GridItem, SimpleGrid, useToast } from '@chakra-ui/react'

// Types
import { Company } from 'types/Company'
import { PersonKeyEnum } from 'types/Person'
import { UserMotoboyRecord } from '../new/types'

type TabPersonalDataProps = {
  endpoint: string
}

export const TabPersonalData = (props: TabPersonalDataProps) => {
  /*
  |-----------------------------------------------------------------------------
  | Constants
  |-----------------------------------------------------------------------------
  |
  |
  */

  const { endpoint } = props

  const { t } = useTranslation('common')
  const history = useHistory()

  const { create } = useCreateRecord({ endpoint })

  const toast = useToast({ position: 'top-right' })

  const { role, addUserRole } = useRole({ roleName: 'LOGISTIC_AGENT' })

  const selectCompanyLogistics = useSelectParser<Company>({
    accessor: { value: 'id', label: 'internalIdentification' },
    endpoint: '/app/company?type=logistics',
  })

  const methods = useForm({
    resolver: yupResolver(schema(t)),
  })

  /*
  |-----------------------------------------------------------------------------
  | Memos
  |-----------------------------------------------------------------------------
  |
  |
  */

  const genresOptions = useMemo(
    () => [
      {
        label: 'Masculino',
        value: 'M',
      },
      {
        label: 'Feminino',
        value: 'F',
      },
    ],
    []
  )

  /*
  |-----------------------------------------------------------------------------
  | Functions
  |-----------------------------------------------------------------------------
  |
  |
  */

  const onSubmit: SubmitHandler<UserMotoboyRecord> = React.useCallback(
    async (data) => {
      if (!role) return

      try {
        const formData = { ...data, roleName: 'LOGISTIC_AGENT' }

        const userCreated = await create(formData)

        if (!userCreated) return

        const multiPart = new FormData()

        const roleDocuments = difference(
          Object.keys(data),
          Object.keys(PersonKeyEnum)
        )

        roleDocuments.forEach((roleDocument) => {
          multiPart.append(roleDocument, get(data, roleDocument))
        })

        await addUserRole(userCreated.id, role.id, multiPart)

        toast({
          title: 'Sucesso',
          description: 'Agente Logístico criado com sucesso',
          status: 'success',
        })

        history.push('/users')
      } catch (error) {
        if (error && error instanceof Array) {
          error.forEach((fieldError) => {
            methods.setError(fieldError.field, { message: fieldError.message })
          })
        }
      }
    },
    [addUserRole, create, history, methods, role, toast]
  )

  /*
  |-----------------------------------------------------------------------------
  | Renders
  |-----------------------------------------------------------------------------
  |
  |
  */

  return (
    <Flex
      as="form"
      h="full"
      flexDirection="column"
      justifyContent="space-between"
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <Controller
        name="status"
        control={methods.control}
        defaultValue={true}
        render={({ field: { onChange, value, ref, name } }) => (
          <FieldToggle
            name={name}
            position="absolute"
            top={-16}
            right={0}
            ref={ref}
            isChecked={value}
            onChange={onChange}
            error={methods.formState.errors.status}
          />
        )}
      />

      <Grid
        my="4"
        gap="10"
        templateColumns={{ lg: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
      >
        <GridItem colSpan={2}>
          <FieldText
            label="Nome"
            error={methods.formState.errors.name}
            isRequired
            {...methods.register('name')}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <FieldText
            canModify={false}
            label="E-mail"
            error={methods.formState.errors.email}
            isRequired
            {...methods.register('email')}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <FieldMask
            label="Telefone"
            mask="(99) 9 9999-9999"
            error={methods.formState.errors.phoneNumber}
            isRequired
            {...methods.register('phoneNumber')}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <FieldMask
            label="CPF"
            mask="999.999.999-99"
            error={methods.formState.errors.documentNumber}
            isRequired
            {...methods.register('documentNumber')}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <Controller
            name="birthdate"
            control={methods.control}
            render={({ field }) => (
              <FieldDate
                label="Data de nascimento"
                name={field.name}
                selected={field.value}
                onChange={field.onChange}
                error={methods.formState.errors.birthdate}
                isRequired
              />
            )}
          />
        </GridItem>

        <GridItem colSpan={2}>
          <FieldRadioButton
            label="Gênero"
            name="gender"
            control={methods.control}
            radioButtonDirection="row"
            options={genresOptions}
            isRequired
          />
        </GridItem>

        <GridItem colSpan={2}>
          <Controller
            name="companyId"
            control={methods.control}
            render={({ field }) => (
              <FieldSelect
                label="Vínculo com empresa logística"
                error={methods.formState.errors.companyId as FieldError}
                isLoading={selectCompanyLogistics.isLoading}
                options={selectCompanyLogistics.options}
                isRequired
                {...field}
              />
            )}
          />
        </GridItem>
      </Grid>

      {role && (
        <SimpleGrid columns={2} gap="10">
          <FormRequirementDocumentsFields role={role} methods={methods} />
        </SimpleGrid>
      )}

      <FormFooter isSubmitting={methods.formState.isSubmitting} hasAlert />
    </Flex>
  )
}
